import type { ExchangeRates } from '../est/currency/currency.module';

import { logger } from '../services/logger';

type RangeValue =
  | { more: string }
  | { less: string }
  | { more: string; less: string }
  | { exactly: string };

export interface FilterSpec {
  name: string;
  value: string[] | RangeValue;
}

const UNITS_QUERY_KEY = 'unitsQueryBuilder';

let instance: QueryBuilder;

export class QueryBuilder {
  declare rooms: ValuesSetInput;
  declare statuses: ValuesSetInput;
  declare price: PriceRangeInput;
  declare area: RangeInput;
  declare storey: RangeInput;
  declare price1sqm: RangeInput;

  private declare localStorageService: angular.local.storage.ILocalStorageService;
  private declare defaultCurrency: string;
  private declare exchangeRates: ExchangeRates;

  constructor({
    defaultCurrency,
    exchangeRates,
    localStorageService,
  }: {
    defaultCurrency: string;
    exchangeRates: ExchangeRates;
    localStorageService: angular.local.storage.ILocalStorageService;
  }) {
    this.localStorageService = localStorageService;
    this.defaultCurrency = defaultCurrency;
    this.exchangeRates = exchangeRates;
    this.load();
  }

  static load(
    params: ConstructorParameters<typeof QueryBuilder>[0],
  ): QueryBuilder {
    if (!instance) {
      instance = new QueryBuilder(params);
    }

    return instance;
  }

  static getInstance(): QueryBuilder | null {
    return instance ?? null;
  }

  static hasSavedState(
    localStorageService: angular.local.storage.ILocalStorageService,
  ): boolean {
    return !!localStorageService.get(UNITS_QUERY_KEY);
  }

  toFilters(): FilterSpec[] {
    const filters: FilterSpec[] = [];

    Object.keys(this).forEach((key) => {
      if (typeof this[key].createFilter === 'function') {
        const generatedQuery = this[key].createFilter();

        if (generatedQuery !== null) {
          filters.push(generatedQuery);
        }
      }
    });

    return filters;
  }

  toJSON() {
    return {
      rooms: this.rooms,
      price: this.price,
      statuses: this.statuses,
      area: this.area,
      storey: this.storey,
      price1sqm: this.price1sqm,
    };
  }

  save() {
    this.localStorageService.set(
      UNITS_QUERY_KEY,
      JSON.stringify(this.toJSON()),
    );
  }

  load() {
    let storedQuery: { [key: string]: any } = {};

    try {
      storedQuery =
        JSON.parse(this.localStorageService.get(UNITS_QUERY_KEY) || 'null') ||
        {};
    } catch (error) {
      logger.error('Error restoring unit search filters', {
        error,
      });
    }

    this.rooms = new ValuesSetInput('rooms', {
      ...storedQuery.rooms,
    });
    this.statuses = new ValuesSetInput('status_id', {
      ...storedQuery.statuses,
    });
    this.price = new PriceRangeInput('total_price', {
      ...storedQuery.price,
      defaultCurrency: this.defaultCurrency,
      exchangeRates: this.exchangeRates,
    });
    this.area = new RangeInput('area_total', {
      ...storedQuery.area,
    });
    this.storey = new RangeInput('floor', {
      ...storedQuery.floor,
    });
    this.price1sqm = new RangeInput('1sqmprice', {
      ...storedQuery.price1sqm,
    });
  }

  reset() {
    this.localStorageService.remove(UNITS_QUERY_KEY);

    this.load();
  }

  applyFromFilterSpecs(filters: FilterSpec[]): void {
    // Clear all fields first so stale constraints from a previous state don't bleed through
    this.rooms.value = [];
    this.statuses.value = [];
    this.area.value = null;
    this.storey.value = null;
    this.price1sqm.value = null;
    this.price.value = null;

    // Returns { from, to } with null for missing sides — Angular type="number" requires null not ''
    const toRange = (v: RangeValue): { from: unknown; to: unknown } => {
      if ('exactly' in v)
        return { from: Number(v.exactly), to: Number(v.exactly) };
      if ('more' in v && 'less' in v)
        return { from: Number(v.more), to: Number(v.less) };
      if ('more' in v) return { from: Number(v.more), to: null };
      if ('less' in v) return { from: null, to: Number(v.less) };
      return { from: null, to: null };
    };

    for (const filter of filters) {
      if (Array.isArray(filter.value)) {
        if (filter.name === 'rooms') this.rooms.value = filter.value;
        else if (filter.name === 'status_id')
          this.statuses.value = filter.value;
      } else {
        const range = toRange(filter.value);
        if (filter.name === 'area_total') {
          this.area.value = range as any;
        } else if (filter.name === 'floor') {
          this.storey.value = range as any;
        } else if (filter.name === '1sqmprice') {
          this.price1sqm.value = range as any;
        } else if (filter.name === 'total_price') {
          this.price.value = {
            from:
              range.from !== null
                ? this.price.restoreFromBaseCurrency(Number(range.from))
                : (null as any),
            to:
              range.to !== null
                ? this.price.restoreFromBaseCurrency(Number(range.to))
                : (null as any),
          };
        }
      }
    }
  }
}

class ValuesSetInput {
  declare name: string;
  declare value: string[];

  constructor(name: string, { value = [] }: { value?: string[] } = {}) {
    this.name = name;
    this.value = value;
  }

  createFilter() {
    if (this.value.length > 0) {
      return {
        name: this.name,
        value: this.value,
      };
    }

    return null;
  }

  toJSON() {
    return {
      value: this.value,
    };
  }

  addSelection(item: string) {
    if (!this.value.includes(item)) {
      this.value.push(item);
    }
  }

  toggleSelection(item: string) {
    const index = this.value.indexOf(item);

    if (index >= 0) {
      this.value.splice(index, 1);
    } else {
      this.value.push(item);
    }
  }

  containsSelection(item: string) {
    return this.value.includes(item);
  }
}

class RangeInput {
  declare name: string;
  declare value: { from: string; to: string } | null;

  constructor(
    name: string,
    {
      value = null,
    }: {
      value?: { from: string; to: string } | null;
    } = {},
  ) {
    this.name = name;
    this.value = value;
  }

  createFilter(): { name: string; value: RangeValue } | null {
    return RangeInput.createRangeFilter(this.name, this.value);
  }

  toJSON() {
    return {
      value: this.value,
    };
  }

  static createRangeFilter(
    name: string,
    value: { from: string; to: string } | null,
  ): { name: string; value: RangeValue } | null {
    if (value === null) {
      return null;
    }

    if (value.from && value.to) {
      if (value.from === value.to) {
        return {
          name,
          value: { exactly: value.from },
        };
      }

      const [more, less] = [value.from, value.to]
        .map(Number)
        .sort()
        .map(String);

      return {
        name,
        value: { more, less },
      };
    }

    if (value.from) {
      return {
        name,
        value: { more: value.from },
      };
    }

    if (value.to) {
      return {
        name,
        value: { less: value.to },
      };
    }

    return null;
  }
}

interface PriceConversionOptions {
  convertToBaseCurrency?: boolean;
}

class PriceRangeInput {
  declare currency: string;
  declare name: string;
  declare value: { from: string; to: string } | null;

  private declare defaultCurrency: string;
  private declare exchangeRates: ExchangeRates;
  private exchangeRate = 1;
  private multiplier = 1000;

  constructor(
    name: string,
    {
      value = null,
      currency,
      defaultCurrency,
      exchangeRates,
    }: {
      value?: { from: string; to: string } | null;
      currency?: string;
      defaultCurrency: string;
      exchangeRates: ExchangeRates;
    },
  ) {
    this.name = name;
    this.value = value;
    this.defaultCurrency = defaultCurrency;
    this.exchangeRates = exchangeRates;

    this.setCurrency(currency || defaultCurrency);
  }

  setCurrency(currency: string) {
    this.currency = currency;
    this.multiplier = currency === 'VND' ? 1000000 : 1000;

    if (currency === this.defaultCurrency) {
      this.exchangeRate = 1;
    } else {
      this.exchangeRate =
        this.exchangeRates[`${this.defaultCurrency}/${currency}`];
    }
  }

  /**
   * Converts `from` value to requested currency. Can be used in templates
   */
  getFrom(options?: PriceConversionOptions) {
    return this.value ? this.convertValue(this.value.from, options) : '';
  }

  /**
   * Converts `to` value to requested currency. Can be used in templates
   */
  getTo(options?: PriceConversionOptions) {
    return this.value ? this.convertValue(this.value.to, options) : '';
  }

  convertValue(
    value: string,
    { convertToBaseCurrency = false }: PriceConversionOptions = {},
  ) {
    if (!value) {
      return '';
    }

    let { multiplier } = this;

    if (convertToBaseCurrency) {
      multiplier *= this.exchangeRate;
    }

    return String(Number(value) * multiplier);
  }

  /**
   * Reverses convertValue({ convertToBaseCurrency: true }).
   * Stored filter values are in base-currency raw units (userInput × multiplier × exchangeRate).
   * Returns the UI string (in selected currency, in display units).
   */
  restoreFromBaseCurrency(rawValue: number): string {
    return String(rawValue / (this.multiplier * this.exchangeRate));
  }

  createFilter(): { name: string; value: RangeValue } | null {
    if (this.value === null) {
      return null;
    }

    return RangeInput.createRangeFilter(this.name, {
      from: this.getFrom({ convertToBaseCurrency: true }),
      to: this.getTo({ convertToBaseCurrency: true }),
    });
  }

  toJSON() {
    return {
      value: this.value,
      currency: this.currency,
    };
  }
}
